import type { PlanNode, TreeSection } from '../types';

export interface XY {
  x: number;
  y: number;
}

export interface Viewport {
  x: number;
  y: number;
  zoom: number;
}

export interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
  /** Where the side handles sit, down from the top; absent means the vertical center. */
  handleY?: number;
}

export type Side = 'left' | 'right';

export const MIN_ZOOM = 0.2;
export const MAX_ZOOM = 2.5;
/** The handle is a 10px circle centered on the card edge; edges meet its outer rim. */
export const HANDLE_RADIUS = 5;

export const clampZoom = (k: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, k));

/** A pane-relative screen point to flow coordinates. */
export const screenToFlow = (p: XY, vp: Viewport): XY => ({
  x: (p.x - vp.x) / vp.zoom,
  y: (p.y - vp.y) / vp.zoom,
});

export const flowToScreen = (p: XY, vp: Viewport): XY => ({
  x: p.x * vp.zoom + vp.x,
  y: p.y * vp.zoom + vp.y,
});

/** Zoom to `zoom` keeping the pane point `p` over the same flow point: t' = p - (p - t) * (k'/k). */
export const zoomAt = (vp: Viewport, p: XY, zoom: number): Viewport => {
  const k = clampZoom(zoom);
  const r = k / vp.zoom;
  return { x: p.x - (p.x - vp.x) * r, y: p.y - (p.y - vp.y) * r, zoom: k };
};

/**
 * A horizontal-handle bezier from a right-facing source to a left-facing
 * target. Control points sit max(50, |dx|/2) out along each handle, so a
 * target behind its source still loops out and back instead of folding flat.
 */
export const bezierPath = (sx: number, sy: number, tx: number, ty: number) => {
  const off = Math.max(50, Math.abs(tx - sx) / 2);
  const c1x = sx + off;
  const c2x = tx - off;
  // Cubic at t = 0.5: 0.125 s + 0.375 c1 + 0.375 c2 + 0.125 t.
  const labelX = 0.125 * sx + 0.375 * c1x + 0.375 * c2x + 0.125 * tx;
  const labelY = 0.5 * sy + 0.5 * ty;
  return { path: `M${sx},${sy} C${c1x},${sy} ${c2x},${ty} ${tx},${ty}`, labelX, labelY };
};

/** Where an edge meets a node's handle, in flow units. */
export const handlePosition = (node: Box, side: Side): XY => ({
  x: side === 'right' ? node.x + node.width + HANDLE_RADIUS : node.x - HANDLE_RADIUS,
  y: node.y + (node.handleY ?? node.height / 2),
});

/** Normalize two corners into a box with non-negative size. */
export const rectFromPoints = (a: XY, b: XY): Box => ({
  x: Math.min(a.x, b.x),
  y: Math.min(a.y, b.y),
  width: Math.abs(b.x - a.x),
  height: Math.abs(b.y - a.y),
});

/** Ids of the boxes the rect touches at all (partial overlap selects). */
export const nodesInRect = (boxes: Record<string, Box>, rect: Box): string[] =>
  Object.keys(boxes).filter((id) => {
    const b = boxes[id];
    const w = Math.min(b.x + b.width, rect.x + rect.width) - Math.max(b.x, rect.x);
    const h = Math.min(b.y + b.height, rect.y + rect.height) - Math.max(b.y, rect.y);
    return w > 0 && h > 0;
  });

export const boundsOf = (boxes: Box[]): Box | null => {
  if (boxes.length === 0) return null;
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  boxes.forEach((b) => {
    x0 = Math.min(x0, b.x);
    y0 = Math.min(y0, b.y);
    x1 = Math.max(x1, b.x + b.width);
    y1 = Math.max(y1, b.y + b.height);
  });
  return { x: x0, y: y0, width: x1 - x0, height: y1 - y0 };
};

/** The viewport that centers `bounds` in a W x H pane, never zooming in past 1. */
export const fitViewport = (bounds: Box, W: number, H: number, padding = 0.1): Viewport => {
  const w = Math.max(bounds.width, 1);
  const h = Math.max(bounds.height, 1);
  const zoom = clampZoom(Math.min((W * (1 - 2 * padding)) / w, (H * (1 - 2 * padding)) / h, 1));
  return {
    x: W / 2 - (bounds.x + w / 2) * zoom,
    y: H / 2 - (bounds.y + h / 2) * zoom,
    zoom,
  };
};

const COL = 280;
const ROW = 110;
/** Unplaced topics wrap to a new row after this many. */
const PER_ROW = 6;

/**
 * Display positions for nodes nobody has placed. The API defaults x and y to
 * 0, so every node added from the document view sits at the origin, and a
 * canvas that drew them there would open on one pile. A node at exactly (0,0)
 * counts as unplaced; `toSaved` keeps a real drop off that point.
 *
 * Unplaced accepted nodes go left to right in flow order (section number),
 * six to a row; proposed ones start on the row after. All sit to the right of
 * anything already placed. Nothing is saved until the node is moved.
 */
export const layoutPositions = (nodes: PlanNode[], sections: TreeSection[]): Record<string, XY> => {
  const out: Record<string, XY> = {};
  const unplaced = new Set<string>();
  const placed: Box[] = [];
  nodes.forEach((n) => {
    if (n.x === 0 && n.y === 0) unplaced.add(n.id);
    else {
      out[n.id] = { x: n.x, y: n.y };
      placed.push({ x: n.x, y: n.y, width: 220, height: 60 });
    }
  });
  if (unplaced.size === 0) return out;

  const b = boundsOf(placed);
  const ox = b ? b.x + b.width + 120 : 0;
  const oy = b ? b.y : 0;
  let i = 0;
  const put = (id: string) => {
    out[id] = { x: ox + (i % PER_ROW) * COL, y: oy + Math.floor(i / PER_ROW) * ROW };
    i += 1;
    unplaced.delete(id);
  };
  sections.forEach((s) => {
    if (unplaced.has(s.id)) put(s.id);
  });
  // Proposed nodes (and anything the flow did not place) from the next fresh row.
  if (i % PER_ROW !== 0) i += PER_ROW - (i % PER_ROW);
  nodes.forEach((n) => {
    if (unplaced.has(n.id)) put(n.id);
  });
  return out;
};

/** A drop position as saved: whole units, and never exactly (0,0), which reads as unplaced. */
export const toSaved = (p: XY): XY => {
  const x = Math.round(p.x);
  const y = Math.round(p.y);
  return x === 0 && y === 0 ? { x: 0, y: 1 } : { x, y };
};
